import { mustExist } from '@getstep/sdk/dist/util/Assert';
import type { FunctionComponent } from 'react';
import React, { Suspense } from 'react';
import { Section } from '../../components/layout/section';
import { SectionBlock } from '../../components/layout/section-block';
import { SectionVisual } from '../../components/layout/section-visual';
import { MarkdownRenderer } from '../../components/markdown-renderer';
import type { SectionFragment } from '../../graphql/fragments/SectionFragment.graphql';
import { CustomFieldsetId } from '../../graphql/hooks/form-page';
import { EMPTY_SECTION } from '../../graphql/placeholders';
import { legendId } from '../forms/legend';
import type { FormProps } from '../forms/props';
import { SelfFetchingRichText } from '../rich-text';
import { SectionFeature } from '../section-of-content/features';
import { fragmentToVisualProps } from '../section-of-content/utils';
import styles from './fieldset.module.scss';

/**
 * This component wraps up a bunch of fields and/or submit button.
 */
const FieldsetInternal: FunctionComponent<Props> = ({
  customId,
  next,
  renderSubmit,
  loading,
  section,
  meta,
  renderInsideGrid = true
}) => {
  const Component = mustExist(FIELDSETS[customId], `Fieldset ${customId} component not found`);
  const {
    title,
    subtitle,
    image,
    content,
    feature
  } = section ?? EMPTY_SECTION;
  const formComponent = <Suspense fallback={null}>
            <Component next={next} renderSubmit={renderSubmit} title={title} subtitle={content ? <SelfFetchingRichText {...content} /> : subtitle && <MarkdownRenderer source={subtitle} />} meta={meta} />
        </Suspense>;
  if (!renderInsideGrid) {
    return formComponent;
  }
  return <Section grid loading={loading} className={styles.fieldset} tag='div' role='group' aria-labelledby={legendId(customId)} data-sentry-element="Section" data-sentry-component="FieldsetInternal" data-sentry-source-file="fieldset.tsx">
            <SectionBlock align='start' justify='start' className={styles.fields} data-sentry-element="SectionBlock" data-sentry-source-file="fieldset.tsx">
                <div className={styles.fieldbox}>{formComponent}</div>
            </SectionBlock>

            {section && <SectionBlock className={styles.visual} justify='center'>
                    <SectionFeature feature={feature} fallback={<SectionVisual image={image} {...fragmentToVisualProps(section)} />} />
                </SectionBlock>}
        </Section>;
};
const FIELDSETS = {
  [CustomFieldsetId.LoginEnterPhone]: React.lazy(() => import('../forms/enter-phone').then(module => ({
    default: module.EnterPhone
  }))),
  [CustomFieldsetId.LoginEnterEmail]: React.lazy(() => import('../forms/enter-email').then(module => ({
    default: module.EnterEmail
  }))),
  [CustomFieldsetId.LoginEnterEmailCode]: React.lazy(() => import('../forms/enter-code/enter-email-code').then(module => ({
    default: module.EnterEmailCode
  }))),
  [CustomFieldsetId.LoginEnterCode]: React.lazy(() => import('../forms/enter-code/enter-phone-code').then(module => ({
    default: module.EnterPhoneCode
  }))),
  [CustomFieldsetId.LoginChooseSso]: React.lazy(() => import('../forms/choose-sso').then(module => ({
    default: module.ChooseSSO
  }))),
  [CustomFieldsetId.KycEnterAddress]: React.lazy(() => import('../forms/identity/enter-address').then(module => ({
    default: module.EnterAddress
  }))),
  [CustomFieldsetId.KycEnterLastFourSsn]: React.lazy(() => import('../forms/identity/enter-last-four-ssn').then(module => ({
    default: module.EnterLastFourSsn
  }))),
  [CustomFieldsetId.KycEnterFullSsn]: React.lazy(() => import('../forms/identity/enter-full-ssn').then(module => ({
    default: module.EnterFullSsn
  }))),
  [CustomFieldsetId.KycEnterPersonalInfo]: React.lazy(() => import('../forms/identity/enter-personal-info').then(module => ({
    default: module.EnterPersonalInfo
  }))),
  [CustomFieldsetId.SchoolRequestWaitlist]: React.lazy(() => import('../forms/school-request-waitlist').then(module => ({
    default: module.SchoolRequestWaitlist
  }))),
  [CustomFieldsetId.StepBlackWaitlist]: React.lazy(() => import('../forms/step-black-waitlist').then(module => ({
    default: module.StepBlackWaitlist
  }))),
  [CustomFieldsetId.SavingsCalculator]: React.lazy(() => import('../forms/savings-calculator').then(module => ({
    default: module.SavingsCalculatorForm
  })))
};
type Props = Pick<FormProps<unknown>, 'renderSubmit' | 'next'> & {
  customId: CustomFieldsetId;
  preset?: 'primary' | 'default';
  section?: SectionFragment;
  loading?: boolean;
  renderInsideGrid?: boolean;
  meta?: JSON;
};
export default FieldsetInternal;